import * as React from "react";
import { createBrowserRouter, Route, RouterProvider, Routes, Navigate } from "react-router-dom";

import { ROUTES } from "../_constant/screens";

import ErrorPage from "../containers/ErrorPage";

import SwitchAccountByUrl from "./SwitchAccountByUrl";
import AppRedirect from "./redirection/AppRedirect";
import ProtectedRoute from "./ProtectedRoute";
import ErrorBoundary from "../containers/ErrorBoundary/ErrorBoundary";
import CustomDragLayer from "../components/CustomDragLayer";
import ScrollToTop from "../components/ScrollToTop/ScrollToTop";
import { LogoutRoute } from "./LogoutRoute";
import { GlobalLoader } from "../components/Loader";

// Each page loads its own code the first time it opens, so the first visit downloads less.
const Visits = React.lazy(() => import("../pages/Visits/Visits"));
const Customers = React.lazy(() => import("../pages/Customers/Customers"));
const NewCustomer = React.lazy(() => import("../pages/NewCustomer/NewCustomer"));
const EditCustomer = React.lazy(() => import("../pages/EditCustomer/EditCustomer"));
const NewVisit = React.lazy(() => import("../pages/NewVisit/NewVisit"));
const EditVisit = React.lazy(() => import("../pages/EditVisit/EditVisit"));
const AllAssetsWrapper = React.lazy(() => import("../containers/assets/all/AllAssetsWrapper"));
const AllActivities = React.lazy(() => import("../containers/activities/all/AllActivities"));
const ChangePasswordPage = React.lazy(() => import("../containers/ChangePassword"));
const ActivityPage = React.lazy(() => import("../containers/activities/ActivityPage"));
const VerifyEmail = React.lazy(() => import("../pages/VerifyEmail/VerifyEmail"));
const AssetPage = React.lazy(() => import("../containers/assets/AssetPage"));
const SwitchAccountPage = React.lazy(() => import("../pages/SwitchAccountPage"));
const AllProjects = React.lazy(() => import("../containers/projects/all/AllProjects"));
const NewProject = React.lazy(() => import("../pages/NewProject/NewProject"));
const EditProject = React.lazy(() => import("../pages/EditProject/EditProject"));
const Vendors = React.lazy(() => import("../pages/Vendors/Vendors"));
const NewVendor = React.lazy(() => import("../pages/NewVendor/NewVendor"));
const EditVendor = React.lazy(() => import("../pages/EditVendor/EditVendor"));
const Settings = React.lazy(() => import("../pages/Settings/Settings"));

const AppRoutes: React.FC = () => {
  return (
    <>
      <CustomDragLayer />
      <ErrorBoundary>
        <SwitchAccountByUrl />
        <ScrollToTop />
        <React.Suspense fallback={<GlobalLoader />}>
          <Routes>
            <Route path="/" element={<ProtectedRoute component={AppRedirect} withoutWrapper />} />

            <Route
              path={`${ROUTES.SINGLE_ACTIVITY}`}
              element={<Navigate to={ROUTES.ACTIVITIES} />}
            />
            <Route
              path={ROUTES.ACTIVITIES}
              element={<ProtectedRoute component={AllActivities} />}
            />
            <Route
              path={`${ROUTES.SINGLE_ACTIVITY}/*`}
              element={<ProtectedRoute component={ActivityPage} />}
            />

            <Route path={`${ROUTES.SINGLE_ASSET}`} element={<Navigate to={ROUTES.ASSETS} />} />
            <Route
              path={`${ROUTES.ASSETS}`}
              element={<ProtectedRoute component={AllAssetsWrapper} />}
            />
            <Route
              path={`${ROUTES.SINGLE_ASSET}/*`}
              element={<ProtectedRoute component={AssetPage} />}
            />

            <Route
              path={`${ROUTES.SETTINGS}/*`}
              element={<ProtectedRoute component={Settings} />}
            />

            <Route path={`${ROUTES.SINGLE_VISIT}`} element={<Navigate to={ROUTES.VISITS} />} />
            <Route path={ROUTES.VISITS} element={<ProtectedRoute component={Visits} />} />
            <Route path={ROUTES.CREATE_VISIT} element={<ProtectedRoute component={NewVisit} />} />
            <Route
              path={`${ROUTES.SINGLE_VISIT}/:id`}
              element={<ProtectedRoute component={EditVisit} />}
            />

            <Route
              path={`${ROUTES.SINGLE_CUSTOMER}`}
              element={<Navigate to={ROUTES.CUSTOMERS} />}
            />
            <Route path={ROUTES.CUSTOMERS} element={<ProtectedRoute component={Customers} />} />
            <Route
              path={ROUTES.CREATE_CUSTOMER}
              element={<ProtectedRoute component={NewCustomer} />}
            />
            <Route
              path={`${ROUTES.SINGLE_CUSTOMER}/:id`}
              element={<ProtectedRoute component={EditCustomer} />}
            />

            <Route path={`${ROUTES.SINGLE_VENDOR}`} element={<Navigate to={ROUTES.VENDORS} />} />
            <Route path={ROUTES.VENDORS} element={<ProtectedRoute component={Vendors} />} />
            <Route path={ROUTES.CREATE_VENDOR} element={<ProtectedRoute component={NewVendor} />} />
            <Route
              path={`${ROUTES.SINGLE_VENDOR}/:id`}
              element={<ProtectedRoute component={EditVendor} />}
            />

            <Route path={`${ROUTES.SINGLE_PROJECT}`} element={<Navigate to={ROUTES.PROJECTS} />} />
            <Route path={ROUTES.PROJECTS} element={<ProtectedRoute component={AllProjects} />} />
            <Route
              path={ROUTES.CREATE_PROJECT}
              element={<ProtectedRoute component={NewProject} />}
            />
            <Route
              path={`${ROUTES.SINGLE_PROJECT}/:id`}
              element={<ProtectedRoute component={EditProject} />}
            />

            <Route path={`${ROUTES.CHANGE_ACCOUNT}`} element={<SwitchAccountPage />} />

            <Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePasswordPage />} />
            <Route path={ROUTES.LOGOUT} element={<LogoutRoute />} />
            {/* <Route path={`/mock`} element={<MockRoute />} /> */}

            <Route
              path={ROUTES.ERROR}
              element={
                <ErrorPage
                  bgImage={"wrong"}
                  title={"Something went wrong"}
                  message={"We're working on it and we'll get it fixed\nas soon as we can."}
                  buttonText={"Reload app"}
                />
              }
            />

            <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />

            <Route
              path="/404"
              element={
                <ErrorPage
                  bgImage={"error"}
                  title={"The page you were looking for doesn’t exist"}
                  message={"You may have typed the address\nincorrectly or the page has moved."}
                  buttonText={"Reload app"}
                />
              }
            />
            <Route path="*" element={<Navigate to="/404" />} />
          </Routes>
        </React.Suspense>
      </ErrorBoundary>
    </>
  );
};

// A data router, so that hooks such as useBlocker work; the app's own routes stay declared with <Routes> above.
const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

const AppRouter: React.FC = () => <RouterProvider router={router} />;

export default AppRouter;
